import type { TrackerConfig } from './contracts';
import type { TrackerLogger } from './logger';
import {
  COMPACT_CONTEXT_VERSION, CONTEXT_VERSION, WORKSPACE_PREFIX_LENGTH,
  encodeTrackingContext, parseTrackingContext
} from './context';

const LINKER_PARAMETER = '_upl';
// A followed link arrives within seconds. The window bounds the reuse of a copied or shared URL.
const MAX_LINK_AGE_MS = 120_000;
const MAX_LINK_FUTURE_MS = 60_000;

type LinkerConfig = Pick<
  TrackerConfig,
  'workspaceId' | 'privacyMode' | 'linkedDomains' | 'linkAnonymousSessions'
>;
type LinkedIds = { visitorId: string; sessionId: string };
type Disposer = () => void;

type InboundLinkDecision =
  | { status: 'accepted'; visitorId: string; sessionId: string }
  | {
    status: 'rejected';
    reason: 'malformed' | 'version' | 'expired' | 'workspace' | 'mode' | 'linking_disabled' | 'anonymous_linking_disabled' | 'referrer';
  };

type InboundLink = { value: string; receivedAt: number; referrer: string };

const parseUrl = (value: string, base?: string): URL | null => {
  try {
    return new URL(value, base);
  } catch {
    return null;
  }
};

const normalizeHostname = (hostname: string): string => hostname.trim().toLowerCase().replace(/\.$/, '');

const isLocalOrIpHost = (hostname: string): boolean => hostname === 'localhost'
  || hostname.endsWith('.localhost')
  || hostname.startsWith('[')
  || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);

/** Explicit trust rules apply to both navigation targets and inbound referrers. */
export const isLinkedHost = (
  hostname: string,
  linkedDomains: readonly string[]
): boolean => {
  const host = normalizeHostname(hostname);
  if (!host) return false;
  return linkedDomains.some(domain => domain.startsWith('*.')
    ? host.endsWith(domain.slice(1))
    : host === domain);
};

/** A bare hostname or a leading `*.` rule for all subdomains, excluding the apex. */
export const parseLinkedDomain = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const candidate = value.trim().toLowerCase();
  const wildcard = candidate.startsWith('*.');
  const hostname = wildcard ? candidate.slice(2) : candidate;
  if (!hostname || /[\s/?#@*%\\]/.test(hostname)) return null;
  // URL normalizes away a default port. Reject it before parsing, while allowing bare IPv6 literals.
  if (hostname.includes(':') && !/^\[[0-9a-f:]+\]$/.test(hostname)) return null;
  const url = parseUrl(`http://${hostname}`);
  if (!url || url.port || url.pathname !== '/') return null;
  const normalized = normalizeHostname(url.hostname);
  if (!normalized || normalized.split('.').some(label => !label)) return null;
  if (wildcard && (isLocalOrIpHost(normalized) || !normalized.includes('.'))) return null;
  return wildcard ? `*.${normalized}` : normalized;
};

const linkerPairs = (search: string): string[] => search.replace(/^\?/, '').split('&');

const isLinkerPair = (pair: string): boolean =>
  pair === LINKER_PARAMETER || pair.startsWith(`${LINKER_PARAMETER}=`);

/** Removes every linker pair and keeps the other pairs byte for byte. */
export const removeLinkerParameter = (search: string): string => {
  const pairs = linkerPairs(search);
  if (!pairs.some(isLinkerPair)) return search;
  const kept = pairs.filter(pair => !isLinkerPair(pair)).join('&');
  return kept ? `?${kept}` : '';
};

const readLinkerParameter = (search: string): string | null => {
  const pair = linkerPairs(search).find(isLinkerPair);
  if (pair === undefined) return null;
  const raw = pair.slice(LINKER_PARAMETER.length + 1).replace(/\+/g, ' ');
  try {
    return decodeURIComponent(raw);
  } catch {
    return '';
  }
};

const linkTarget = (href: string, linkedDomains: readonly string[]): URL | null => {
  const url = parseUrl(href, window.location.href);
  if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:')) return null;
  if (url.host === window.location.host) return null;
  return isLinkedHost(url.hostname, linkedDomains) ? url : null;
};

/**
 * The linker value for a navigation to `href`, or null when the target is not
 * a linked host or the ids cannot be carried. Written as the packed format 2
 * (see context.ts); format 1 spells the parts out with dots.
 */
export const linkValueFor = (
  href: string,
  config: LinkerConfig,
  ids: LinkedIds,
  now: number
): string | null => {
  if (config.privacyMode === 'anonymous' && !config.linkAnonymousSessions) return null;
  if (!linkTarget(href, config.linkedDomains)) return null;
  return encodeTrackingContext(config, ids, now);
};

export const withLinkerParameter = (href: string, value: string): string => {
  const url = parseUrl(href, window.location.href);
  if (!url) return href;
  const pair = `${LINKER_PARAMETER}=${encodeURIComponent(value)}`;
  const search = removeLinkerParameter(url.search);
  url.search = search ? `${search}&${pair}` : `?${pair}`;
  return url.href;
};

const formAction = (form: HTMLFormElement, submitter: HTMLElement | null): string => {
  if (
    (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement)
    && submitter.hasAttribute('formaction')
  ) return submitter.formAction;
  return form.action;
};

/**
 * Adds the linker parameter to links and form submissions that leave for a
 * linked host. Anchors are rewritten at click time so the timestamp is fresh.
 */
export const installLinkDecoration = (handlers: {
  linkValue(href: string): string | null;
  onFailure(error: unknown, operation: string): void;
}): Disposer => {
  const anchors = new Map<HTMLAnchorElement, string>();
  const forms = new Map<HTMLFormElement, {
    input: HTMLInputElement;
    value: string;
    original: { status: 'existing'; value: string } | { status: 'created' };
  }>();

  const clearAnchor = (anchor: HTMLAnchorElement): void => {
    const value = anchors.get(anchor);
    if (value === undefined) return;
    const url = parseUrl(anchor.href, window.location.href);
    // Keep changes made by the host application after decoration.
    if (url && readLinkerParameter(url.search) === value) {
      url.search = removeLinkerParameter(url.search);
      anchor.href = url.href;
    }
    anchors.delete(anchor);
  };

  const clearForm = (form: HTMLFormElement): void => {
    const decoration = forms.get(form);
    if (!decoration) return;
    const { input, value, original } = decoration;
    if (input.name === LINKER_PARAMETER && input.type === 'hidden' && input.value === value) {
      if (original.status === 'created') input.remove();
      else input.value = original.value;
    }
    forms.delete(form);
  };

  const handleClick = (event: MouseEvent): void => {
    try {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      clearAnchor(anchor);
      const value = handlers.linkValue(anchor.href);
      if (value) {
        anchor.href = withLinkerParameter(anchor.href, value);
        anchors.set(anchor, value);
      }
    } catch (error) {
      handlers.onFailure(error, 'decorate_link');
    }
  };

  const handleSubmit = (event: SubmitEvent): void => {
    try {
      if (!(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      clearForm(form);
      if (event.defaultPrevented || form.method === 'dialog') return;
      const value = handlers.linkValue(formAction(form, event.submitter ?? null));
      if (!value) return;
      const existing = form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${LINKER_PARAMETER}"]`);
      const input = existing ?? document.createElement('input');
      forms.set(form, {
        input,
        value,
        original: existing ? { status: 'existing', value: existing.value } : { status: 'created' }
      });
      input.type = 'hidden';
      input.name = LINKER_PARAMETER;
      input.value = value;
      if (!existing) form.append(input);
    } catch (error) {
      handlers.onFailure(error, 'decorate_form');
    }
  };

  document.addEventListener('click', handleClick, { capture: true, passive: true });
  window.addEventListener('submit', handleSubmit);
  return () => {
    document.removeEventListener('click', handleClick, { capture: true });
    window.removeEventListener('submit', handleSubmit);
    for (const anchor of anchors.keys()) {
      try {
        clearAnchor(anchor);
      } catch (error) {
        handlers.onFailure(error, 'clear_link_decoration');
      }
    }
    for (const form of forms.keys()) {
      try {
        clearForm(form);
      } catch (error) {
        handlers.onFailure(error, 'clear_form_decoration');
      }
    }
    anchors.clear();
    forms.clear();
  };
};

// The address bar is one per page, so the link it carried is too.
let pendingInboundLink: InboundLink | null = null;

/**
 * Reads the linker parameter, keeps it in memory for the first tracker that
 * starts on this page, and removes it from the address bar at once, valid or not.
 */
export const captureInboundLink = (logger: TrackerLogger): void => {
  const { hash, pathname, search } = window.location;
  const value = readLinkerParameter(search);
  if (value === null) return;
  pendingInboundLink = { value, receivedAt: Date.now(), referrer: document.referrer };
  try {
    history.replaceState(history.state, '', `${pathname}${removeLinkerParameter(search)}${hash}`);
  } catch (error) {
    logger.debug('strip_linker_parameter', {
      outcome: 'failed',
      error: error instanceof Error ? error.message : String(error)
    });
  }
};

const rejected = (reason: Extract<InboundLinkDecision, { status: 'rejected' }>['reason']): InboundLinkDecision =>
  ({ status: 'rejected', reason });

const decideInboundLink = (
  link: InboundLink,
  config: LinkerConfig,
  currentHostname: string
): InboundLinkDecision => {
  if (config.linkedDomains.length === 0) return rejected('linking_disabled');
  const parsed = parseTrackingContext(link.value);
  if (!parsed) return rejected('malformed');
  const { version, mode, workspacePrefix, visitorId, sessionId } = parsed;
  if (version !== CONTEXT_VERSION && version !== COMPACT_CONTEXT_VERSION) return rejected('version');

  const sentAt = parsed.seconds * 1_000;
  if (link.receivedAt - sentAt > MAX_LINK_AGE_MS || sentAt - link.receivedAt > MAX_LINK_FUTURE_MS) {
    return rejected('expired');
  }
  if (workspacePrefix !== config.workspaceId.slice(0, WORKSPACE_PREFIX_LENGTH)) return rejected('workspace');
  if (mode !== (config.privacyMode === 'anonymous' ? 'a' : 'p')) return rejected('mode');
  if (config.privacyMode === 'anonymous' && !config.linkAnonymousSessions) return rejected('anonymous_linking_disabled');

  if (link.referrer) {
    const referrerHost = parseUrl(link.referrer)?.hostname ?? '';
    const trustedReferrer = referrerHost !== ''
      && (normalizeHostname(referrerHost) === normalizeHostname(currentHostname)
        || isLinkedHost(referrerHost, config.linkedDomains));
    if (!trustedReferrer) return rejected('referrer');
  }

  return { status: 'accepted', visitorId, sessionId };
};

/**
 * The ids to continue from a linked page, or null. The first tracker that
 * starts on a page consumes the link, so a later consent change starts fresh.
 */
export const resolveInboundLink = (config: LinkerConfig, logger: TrackerLogger): LinkedIds | null => {
  captureInboundLink(logger);
  const link = pendingInboundLink;
  pendingInboundLink = null;
  if (!link) return null;

  const decision = decideInboundLink(link, config, window.location.hostname);
  if (decision.status === 'rejected') {
    logger.debug('adopt_linked_session', { outcome: 'rejected', reason: decision.reason });
    return null;
  }
  logger.debug('adopt_linked_session', { outcome: 'accepted', privacyMode: config.privacyMode });
  return { visitorId: decision.visitorId, sessionId: decision.sessionId };
};
